if (!customElements.get("quick-add-modal")) {
  /*
   * Quick view markup. Only <product-info> is used, so request the quick-view section alone
   * (?section_id=quick-view, the same request assets/product-variants-gallery.js already makes)
   * instead of the full page with header, footer and layout. A failed section request, or one
   * without <product-info>, falls back once to the original full-page URL. A 429 is not retried:
   * the full page would be rate limited too, and the original code showed nothing on a 429 either.
   * Openers without data-handle keep the original single request.
   * DOMParser keeps the parse inert (no image fetches or custom element upgrades).
   * variantId (the shade picked on the card) is sent as ?variant=, which sections/quick-view.liquid
   * renders instead of the product default: checked swatch, gallery, price, stock and the id that
   * Add to bag submits.
   */
  const quickViewUrl = (productHandle, sectionOnly, variantId) => {
    const root = window.Shopify?.routes?.root || "/";
    const variantParam = variantId ? `&variant=${variantId}` : "";
    return `${root}products/${productHandle}?view=quick-view${variantParam}${sectionOnly ? "&section_id=quick-view" : ""}`;
  };

  const parseQuickViewProductInfo = (responseText) =>
    new DOMParser().parseFromString(responseText, "text/html").querySelector("product-info");

  /*
   * Circuit breaker for the warm-up below, the same idea and duration as the gallery one in
   * assets/product-variants-gallery.js: after the storefront answers a quick view request with 429
   * or 5xx, no warm-ups for a minute (a shopper's click still fetches). A warm-up's 429 resolves
   * empty like any other 429 here. Only the click that follows its press within a second takes that
   * (and shows the retry message) instead of asking again straight into the rate limit; a later
   * click makes its own request. A warm-up's 5xx rejects instead of escalating to the heavier full
   * page on a press that may not become a click; the click that takes it (takeQuickViewProductInfo)
   * then requests the full page, the step a failed section request takes anyway.
   */
  const QUICK_VIEW_BACKOFF_MS = 60000;
  let quickViewBackoffUntil = 0;
  const quickViewWarmUpAllowed = () => Date.now() >= quickViewBackoffUntil;
  const checkQuickViewResponse = (response, warmUp) => {
    if (response.status === 429 || response.status >= 500) {
      quickViewBackoffUntil = Date.now() + QUICK_VIEW_BACKOFF_MS;
      if (warmUp && response.status !== 429) {
        throw Object.assign(new Error(`Quick view warm-up got ${response.status}`), { tryFullPage: true });
      }
    }
    return response;
  };

  const fetchQuickViewPage = (productHandle, variantId, warmUp = false) =>
    fetch(quickViewUrl(productHandle, false, variantId))
      .then((response) => checkQuickViewResponse(response, warmUp).text())
      .then(parseQuickViewProductInfo);

  const fetchQuickViewProductInfo = (productHandle, variantId, warmUp = false) => {
    if (!productHandle) return fetchQuickViewPage(productHandle);

    return fetch(quickViewUrl(productHandle, true, variantId)).then((response) => {
      checkQuickViewResponse(response, warmUp);
      if (!response.ok && response.status !== 429) return fetchQuickViewPage(productHandle, variantId, warmUp);
      return response.text().then((responseText) => {
        const productElement = parseQuickViewProductInfo(responseText);
        if (productElement || !response.ok) return productElement;
        return fetchQuickViewPage(productHandle, variantId, warmUp);
      });
    });
  };

  /*
   * The shade the card shows as picked. assets/product-card-swatches.js moves .selected-swatch to
   * the tapped swatch and writes its id into the card's add-to-bag form (.product-variant-id); an
   * untouched card holds its default variant there, the one quick-view.liquid falls back to anyway.
   */
  const cardSelectedVariantId = (opener) => {
    const card = opener?.closest(".featured-card-product");
    if (!card) return "";
    const variantId =
      card.querySelector(".product-swatch.selected-swatch")?.dataset.id ||
      card.querySelector(".product-variant-id")?.value ||
      "";
    return /^\d+$/.test(variantId) ? variantId : "";
  };

  /*
   * Start the quick view request on pointerdown so it overlaps the ~100ms until the click that
   * opens the modal. One entry per handle and shade, used once and only while under 10s old (then
   * dropped, so no parsed document is kept alive), so a warm-up without a click costs at most one
   * extra request per product shade per 10s.
   * A touch waits 100ms first: a touch that starts a scroll gets pointercancel in that time and
   * requests nothing, and a tap that lifts sooner starts it on pointerup, ahead of its click.
   */
  const QUICK_VIEW_TOUCH_WARM_UP_DELAY_MS = 100;
  const QUICK_VIEW_PREFETCH_MAX_AGE_MS = 10000;
  // An empty warm-up (a 429) is kept only long enough for its own press to become a click.
  const QUICK_VIEW_EMPTY_PREFETCH_MAX_AGE_MS = 1000;
  const quickViewPrefetches = new Map();
  const quickViewPrefetchKey = (productHandle, variantId) => `${productHandle}|${variantId || ""}`;

  const takeQuickViewProductInfo = (productHandle, variantId) => {
    const key = quickViewPrefetchKey(productHandle, variantId);
    const prefetch = productHandle ? quickViewPrefetches.get(key) : null;
    if (prefetch) quickViewPrefetches.delete(key);
    if (prefetch && Date.now() < prefetch.expiresAt) {
      return prefetch.promise.catch((error) =>
        error?.tryFullPage
          ? fetchQuickViewPage(productHandle, variantId)
          : fetchQuickViewProductInfo(productHandle, variantId),
      );
    }
    return fetchQuickViewProductInfo(productHandle, variantId);
  };

  const warmUpQuickView = (productHandle, variantId) => {
    if (!quickViewWarmUpAllowed()) return;
    const key = quickViewPrefetchKey(productHandle, variantId);
    const existing = quickViewPrefetches.get(key);
    if (existing && Date.now() < existing.expiresAt) return;

    const promise = fetchQuickViewProductInfo(productHandle, variantId, true);
    const entry = { expiresAt: Date.now() + QUICK_VIEW_PREFETCH_MAX_AGE_MS, promise };
    const drop = () => {
      if (quickViewPrefetches.get(key) === entry) quickViewPrefetches.delete(key);
    };
    quickViewPrefetches.set(key, entry);
    setTimeout(drop, QUICK_VIEW_PREFETCH_MAX_AGE_MS);
    // A press that never became a click (dragged off, cancelled by a scroll) must not leave its 429
    // for a click seconds later, by when the rate limit may have cleared.
    promise.then(
      (productElement) => {
        if (productElement) return;
        entry.expiresAt = Math.min(entry.expiresAt, Date.now() + QUICK_VIEW_EMPTY_PREFETCH_MAX_AGE_MS);
        setTimeout(drop, QUICK_VIEW_EMPTY_PREFETCH_MAX_AGE_MS);
      },
      () => {},
    );
  };

  let pendingTouchWarmUp = null;

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.button !== 0 || !(event.target instanceof Element)) return;
      const button = event.target.closest("modal-opener button[data-handle]");
      // aria-disabled: this opener's quick view is already loading (or waiting for its stylesheet).
      if (!button || button.getAttribute("aria-disabled") === "true") return;
      const modalSelector = button.closest("modal-opener")?.getAttribute("data-modal") || "";
      if (!modalSelector.includes("QuickAdd")) return;

      const productHandle = button.getAttribute("data-handle");
      if (!productHandle || !quickViewWarmUpAllowed()) return;
      const variantId = cardSelectedVariantId(button);
      const start = () => warmUpQuickView(productHandle, variantId);
      if (event.pointerType !== "touch") {
        start();
        return;
      }

      if (pendingTouchWarmUp) clearTimeout(pendingTouchWarmUp.timer);
      const pending = { pointerId: event.pointerId, button, start };
      pending.timer = setTimeout(() => {
        if (pendingTouchWarmUp !== pending) return;
        pendingTouchWarmUp = null;
        start();
      }, QUICK_VIEW_TOUCH_WARM_UP_DELAY_MS);
      pendingTouchWarmUp = pending;
    },
    { capture: true, passive: true },
  );

  const endTouchWarmUp = (event) => {
    if (!pendingTouchWarmUp || event.pointerId !== pendingTouchWarmUp.pointerId) return;
    const { timer, button, start } = pendingTouchWarmUp;
    clearTimeout(timer);
    pendingTouchWarmUp = null;
    if (event.type !== "pointerup") return;
    // A touch pointer is captured by the element it pressed, so event.target is that button wherever
    // the finger lifted. Only a lift on the button can become its click.
    const rect = button.getBoundingClientRect();
    const liftedOnButton =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (liftedOnButton) start();
  };
  document.addEventListener("pointerup", endTouchWarmUp, { capture: true, passive: true });
  document.addEventListener("pointercancel", endTouchWarmUp, { capture: true, passive: true });

  /*
   * quick-add.css loads non-blocking (media="print" until its onload, snippets/header-search.liquid
   * and layout/theme.liquid), and the inline copy in layout/theme.liquid only has the closed modal.
   * Opening before the sheet had arrived locked the page scroll behind a modal nobody could see
   * (it appeared, without focus, once the sheet landed), so an early open switches the sheet to
   * media all and waits for it, for 3s at most.
   * Returns null when the sheet already applies (or after one wait has run out: from then on the
   * modal opens straight away, as it did before).
   * A copy counts as loaded once its load event has fired. link.sheet cannot tell: Firefox sets it
   * as soon as the file starts loading. A copy that loaded before this deferred script ran is no
   * longer media="print" (its onload switched it), or is a blocking stylesheet_tag copy, which
   * deferred scripts wait for. A copy whose request failed (404, blocked, offline) never fires
   * again, so there is nothing to wait for and the modal opens at once, as it always did. One that
   * failed before this script ran is still media="print" but its request has finished (it has a
   * resource timing entry); a copy in that state that did load is about to switch, so opening at
   * once is right for it too.
   */
  const QUICK_ADD_STYLESHEET_WAIT_MS = 3000;
  const QUICK_ADD_STYLESHEET_SELECTOR = 'link[rel="stylesheet"][href*="/quick-add.css"]';
  let quickAddStylesheetWaitExpired = false;
  // The one open waiting for the sheet: { modal, opener }.
  let pendingQuickAddOpen = null;
  const loadedQuickAddStylesheets = new WeakSet();
  const failedQuickAddStylesheets = new WeakSet();
  document.querySelectorAll(QUICK_ADD_STYLESHEET_SELECTOR).forEach((link) => {
    if (link.media !== "print") loadedQuickAddStylesheets.add(link);
    else if (performance.getEntriesByName(link.href, "resource").length) failedQuickAddStylesheets.add(link);
  });
  // Capture phase: load and error do not bubble. Also records copies that search results and facets
  // insert.
  const noteQuickAddStylesheet = ({ type, target }) => {
    if (!(target instanceof HTMLLinkElement) || !target.matches(QUICK_ADD_STYLESHEET_SELECTOR)) return;
    (type === "load" ? loadedQuickAddStylesheets : failedQuickAddStylesheets).add(target);
  };
  document.addEventListener("load", noteQuickAddStylesheet, true);
  document.addEventListener("error", noteQuickAddStylesheet, true);

  const waitForQuickAddStylesheet = () => {
    if (quickAddStylesheetWaitExpired) return null;
    const allLinks = Array.from(document.querySelectorAll(QUICK_ADD_STYLESHEET_SELECTOR));
    const loaded = allLinks.find((link) => loadedQuickAddStylesheets.has(link));
    if (loaded) {
      // Loaded, but its own onload has not switched it from print yet.
      if (loaded.media === "print") loaded.media = "all";
      return null;
    }
    // Only copies still loading can still arrive.
    const links = allLinks.filter((link) => !failedQuickAddStylesheets.has(link));
    if (!links.length) return null;

    return new Promise((resolve) => {
      let timer;
      const done = (event) => {
        clearTimeout(timer);
        links.forEach((link) => {
          link.removeEventListener("load", done);
          link.removeEventListener("error", done);
        });
        if (event?.type !== "load") quickAddStylesheetWaitExpired = true;
        resolve();
      };
      timer = setTimeout(done, QUICK_ADD_STYLESHEET_WAIT_MS);
      links.forEach((link) => {
        link.addEventListener("load", done);
        link.addEventListener("error", done);
        link.media = "all";
      });
    });
  };

  /*
   * sections/quick-view.liquid links its own stylesheets (section-main-product.css, the variant picker
   * and swatch sheets), which a collection, search or home page has not loaded before the first quick
   * view. Inserted with the markup, they arrived after it had painted, and the open modal shifted with
   * no input as each one landed (title margins, gallery height, the swatch fieldset). They now load
   * first, while the spinner shows, and the markup replaces the spinner once they have: its own copies
   * then apply straight from the memory cache, so its first paint is the final layout. These copies sit
   * on the modal element, not in the content that hide() empties, so closing during the wait neither
   * cancels their loads nor leaves the wait to run into its timeout; they are removed once the markup
   * is in. A sheet that fails is not waited for, a slow one for QUICK_ADD_STYLESHEET_WAIT_MS at
   * most (the markup then shows as it always did), and a later open finds them cached.
   */
  const loadQuickViewStylesheets = (productElement, host) => {
    const sources = new Map();
    productElement.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
      sources.set(link.getAttribute("href"), link.getAttribute("media"));
    });
    const links = Array.from(sources, ([href, media]) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      if (media) link.media = media;
      return link;
    });
    let timer;
    const ready = Promise.race([
      Promise.all(
        links.map(
          (link) =>
            new Promise((resolve) => {
              link.addEventListener("load", resolve, { once: true });
              link.addEventListener("error", resolve, { once: true });
              host.appendChild(link);
            }),
        ),
      ),
      new Promise((resolve) => {
        timer = setTimeout(resolve, QUICK_ADD_STYLESHEET_WAIT_MS);
      }),
    ]).then(() => clearTimeout(timer));
    return { links, ready };
  };

  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener("product-info:loaded", ({ target }) => {
          if (target && typeof target.addPreProcessCallback === "function") {
            target.addPreProcessCallback(this.preprocessHTML.bind(this));
          }
        });
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = "";
        // Closed before the quick view finished loading (its request or the stylesheet wait): the
        // card's opener is released now instead of when that load settles.
        this.openedBy?.removeAttribute("aria-disabled");
        this.openedBy?.classList.remove("loading");

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      initializeSliderDrag(container) {
        const slider = container.querySelector(".slider");
        if (slider && !slider.classList.contains("drag-initialized")) {
          slider.classList.add("drag-initialized");

          const sliderInstance = slider.closest("slider-component");
          if (sliderInstance && typeof sliderInstance.addDragToScroll === "function") {
            sliderInstance.addDragToScroll();
          }
        }
      }

      show(opener) {
        // Another tap while an early open waits for the stylesheet would request and open it twice.
        if (pendingQuickAddOpen?.modal === this) return;
        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");

        // Requested before any stylesheet wait, so the two overlap.
        const productHandle = opener.getAttribute("data-handle");
        const productInfo = takeQuickViewProductInfo(productHandle, cardSelectedVariantId(opener));
        const stylesheetWait = waitForQuickAddStylesheet();
        if (!stylesheetWait) {
          this.openWithProductInfo(opener, productInfo);
          return;
        }

        // The page still scrolls during the wait, so another card's "+" can be tapped: only the
        // latest tap opens. Two modals opened together share one scroll lock, and closing the top
        // one would unlock the page behind the other.
        if (pendingQuickAddOpen) {
          pendingQuickAddOpen.opener.removeAttribute("aria-disabled");
          pendingQuickAddOpen.opener.classList.remove("loading");
        }
        const pending = { modal: this, opener };
        pendingQuickAddOpen = pending;
        // Handled once the modal opens; this only stops a failure during the wait being reported as unhandled.
        productInfo.catch(() => {});
        stylesheetWait.then(() => {
          if (pendingQuickAddOpen !== pending) return;
          pendingQuickAddOpen = null;
          this.openWithProductInfo(opener, productInfo);
        });
      }

      openWithProductInfo(opener, productInfo) {
        super.show(opener);
        // Closed and opened again while this request loaded: the newer opening owns the modal and
        // its opener, so this one's late answer (success or failure) must not touch either.
        const opening = (this.quickViewOpening = (this.quickViewOpening || 0) + 1);
        let loaderContainer = null;
        if (this.modalContent) {
          this.modalContent.innerHTML = "";
          loaderContainer = document.createElement("div");
          loaderContainer.className = "quick-add__spinner show";
          loaderContainer.innerHTML = '<span class="loader"></span>';
          this.modalContent.appendChild(loaderContainer);
        }

        let sectionStylesheets = [];
        productInfo
          // A network failure (offline, dropped connection) gets the same message as an empty answer.
          .catch(() => null)
          // The spinner stays until the section's stylesheets have loaded (loadQuickViewStylesheets).
          .then((productElement) => {
            if (!productElement) return null;
            const stylesheets = loadQuickViewStylesheets(productElement, this);
            sectionStylesheets = stylesheets.links;
            return stylesheets.ready.then(() => productElement);
          })
          .then((productElement) => {
            // Closed, or opened again, while the request or the stylesheets loaded.
            if (opening !== this.quickViewOpening || !loaderContainer?.isConnected) return;
            if (!productElement) {
              this.showLoadError();
              return;
            }

            const idMap = new Map();
            productElement.querySelectorAll("[id]").forEach((el) => {
              const name = el.getAttribute("name") || "";
              const id = el.getAttribute("id");

              const should_not_add =
                name.includes("product-variant-id") ||
                name.includes("form_type") ||
                name.includes("utf8") ||
                name.includes("id");

              if (id && !should_not_add) {
                idMap.set(id, `${id}--quickview`);
                el.setAttribute("id", idMap.get(id));
              }
            });
            productElement.querySelectorAll("[for], [form], [data-form-id], [aria-labelledby], [aria-describedby], [aria-controls]").forEach((el) => {
              ["for", "form", "data-form-id", "aria-labelledby", "aria-describedby", "aria-controls"].forEach((attribute) => {
                const value = el.getAttribute(attribute);
                if (value) el.setAttribute(attribute, value.split(/\s+/).map((id) => idMap.get(id) || id).join(" "));
              });
            });
            productElement.querySelectorAll("[name]").forEach((el) => {
              const name = el.getAttribute("name") || "";

              const should_not_add =
                name.includes("product-variant-id") ||
                name.includes("form_type") ||
                name.includes("utf8") ||
                name.includes("id") ||
                name.includes("State") ||
                name.includes("Detail");

              if (!should_not_add) {
                el.name = el.name + "--quickview";
              }
            });

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement?.outerHTML);
            this.modalContent.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }));
            const modalSwatches = this.modalContent.querySelectorAll(".swatch-input__input");
            const addToBagContainer = this.modalContent.querySelector(".buy-buttons-container");
            const addToBagForm = addToBagContainer ? addToBagContainer.querySelector("form") : null;

            this.modalContent.querySelectorAll(".tone-button-wrapper").forEach((button) => {
              button.addEventListener("click", () => {
                const input = button.querySelector("input");
                input.checked = true;
                filterShadesQuickView(this.modalContent);
              });
            });

            if (addToBagForm) {
              addToBagForm.classList.add("quick-add-to-bag");
            }

            modalSwatches.forEach((swatch) => {
              swatch.addEventListener("click", (event) => {
                event.preventDefault();
              });
            });

            applyQuickViewGalleryChange(this.modalContent);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .finally(() => {
            // The markup's own copies apply by now (this runs before the next paint).
            sectionStylesheets.forEach((link) => link.remove());
            // hide() already released the opener of a closed opening; a newer one releases its own.
            if (opening !== this.quickViewOpening) return;
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            this.initializeSliderDrag(this);
          });
      }

      // No product came back (a 429, which is not retried, or a failed request). The spinner used to
      // keep turning in the open modal; the shopper can close it and tap again.
      showLoadError() {
        if (!this.modalContent || !this.hasAttribute("open")) return;
        const message = window.variantStrings?.quickViewError;
        if (!message) {
          this.hide();
          return;
        }
        const container = document.createElement("div");
        container.className = "quick-add__spinner show";
        const text = document.createElement("p");
        text.className = "center";
        text.setAttribute("role", "alert");
        text.textContent = message;
        container.appendChild(text);
        this.modalContent.replaceChildren(container);
      }

      preprocessHTML(productElement) {
        if (productElement) {
          productElement.classList.forEach((classApplied) => {
            if (classApplied.startsWith("color-") || classApplied === "gradient")
              this.modalContent.classList.add(classApplied);
          });
          this.removeDOMElements(productElement);
          this.updateImageSizes(productElement);
          this.preventVariantURLSwitching(productElement);
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute("data-update-url", "false");
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector("pickup-availability");
        if (pickupAvailability) pickupAvailability.remove();

        const quickAddHiddenList = productElement.querySelectorAll(".quick-add-hidden");
        quickAddHiddenList.forEach((item) => {
          if (item) item.remove();
        });
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector(".product");
        if (!product) return;
        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        mediaImages.forEach((image) => {
          image?.closest(".lazy-image")?.classList.add("loaded");
          const skeleton = image?.closest(".lazy-image")?.previousElementSibling;
          if (skeleton && skeleton.classList.contains("image-skeleton")) {
            skeleton.classList.add("hidden");
          }
        });

        const desktopColumns = product?.classList.contains("product--columns");
        if (!desktopColumns) return;

        let mediaImageSizes =
          "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";

        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }

        mediaImages.forEach((img) => img.setAttribute("sizes", mediaImageSizes));
      }
    },
  );
}
